import {
  Button,
  Spinner,
  Table,
  TableBody,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui';
import { ArrowDown, ArrowUp, Funnel } from 'lucide-react';
import { DocumentRow } from '../document-row';
import { FieldPeekPanel } from '../field-peek-panel';
import { useDocumentTable, type DocumentTableProps } from './use-document-table';

export type { DocumentTableProps };

export function DocumentTable(props: DocumentTableProps) {
  const t = useDocumentTable(props);

  if (t.shouldShowLoadingSpinner) {
    return (
      <div className="flex items-center justify-center p-8">
        <Spinner size="lg" />
      </div>
    );
  }

  if (t.shouldShowEmptyMessage) {
    return (
      <div className="flex items-center justify-center p-8 text-muted-foreground">
        No documents found
      </div>
    );
  }

  const { fields, documents, sortState, peekValue } = t.data;

  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 overflow-auto">
        <Table>
          <TableHeader className="sticky top-0 bg-background z-10">
            <TableRow>
              <TableHead className="w-16 text-right bg-muted/30 font-medium select-none">
                #
              </TableHead>
              <TableHead
                className="cursor-pointer select-none hover:bg-muted/50"
                onClick={() => t.handleSort('__id__')}
              >
                <div className="flex items-center gap-2">
                  ID
                  {t.isFieldFiltered('__id__') && <Funnel className="h-3 w-3 text-primary" />}
                  {sortState?.field === '__id__' &&
                    (sortState.direction === 'asc' ? (
                      <ArrowDown className="h-3 w-3 text-primary" />
                    ) : (
                      <ArrowUp className="h-3 w-3 text-primary" />
                    ))}
                </div>
              </TableHead>
              {fields.map((field) => (
                <TableHead
                  key={field}
                  className="cursor-pointer select-none hover:bg-muted/50"
                  onClick={() => t.handleSort(field)}
                >
                  <div className="flex items-center gap-2">
                    {field}
                    {t.isFieldFiltered(field) && <Funnel className="h-3 w-3 text-primary" />}
                    {sortState?.field === field &&
                      (sortState.direction === 'asc' ? (
                        <ArrowDown className="h-3 w-3 text-primary" />
                      ) : (
                        <ArrowUp className="h-3 w-3 text-primary" />
                      ))}
                  </div>
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {documents.map((doc, index) => (
              <DocumentRow
                key={doc.id}
                document={doc}
                fields={fields}
                rowNumber={index + 1}
                onClick={() => t.onRowClick(doc)}
                onPeek={t.handlePeek}
              />
            ))}
          </TableBody>
        </Table>
      </div>
      {t.hasMore && (
        <div className="p-4 border-t flex justify-center">
          <Button onClick={t.onLoadMore} disabled={t.loading || t.loadingMore} variant="outline">
            {t.loadingMore ? <Spinner size="sm" /> : 'Load More'}
          </Button>
        </div>
      )}

      {peekValue && (
        <FieldPeekPanel
          open={!!peekValue}
          onOpenChange={(open) => !open && t.closePeek()}
          fieldName={peekValue.fieldName}
          value={peekValue.value}
          fieldType={peekValue.fieldType}
        />
      )}
    </div>
  );
}
